import React, { FC, useEffect, useState } from 'react'
import { z } from 'zod'
import { YotpoReviews } from '../yotpo-reviews'
import { QuickShip, Clodagh } from '../../icons'
import { getSizedImageUrl } from '../../../core/shopify/get-sized-image-url'

export const Swatch = z.object({
  title: z.string().nullable(),
  handle: z.string().nullable(),
  finish: z.string().nullable(),
  url: z.string().nullable(),
  price: z.string().nullable(),
  type: z.string().nullable(),
  src: z.string().nullable().optional()
})
export type Swatch = z.infer<typeof Swatch>

export const Data = z.object({
  swatches: z.array(Swatch)
})
export type Data = z.infer<typeof Data>

export type Tags = {
  is_parent: string
  parent_id: string
}

export type ProductHit = {
  handle: string
  title: string
  product_image: string
  url: string
  id: number
  named_tags: Tags
  meta?: {
    custom?: {
      designer_collaboration?: string
    }
  }
}

export type Props = {
  hit: ProductHit
}

const fetchSwatches = async function (tag: string) {
  const swatchesImages = [
    {
      name: 'Black',
      src: 'https://cdn.shopify.com/s/files/1/0622/6817/8611/files/swatch-metal-black.jpg?v=1663762976'
    },
    {
      name: 'Champagne Bronze',
      src: 'https://cdn.shopify.com/s/files/1/0622/6817/8611/files/swatch-metal-champagne-bronze.jpg?v=1663763006'
    },
    {
      name: 'Gray',
      src: 'https://cdn.shopify.com/s/files/1/0622/6817/8611/files/swatch-metal-gray.jpg?v=1663763030'
    },
    {
      name: 'Oil Rubbed Bronze',
      src: 'https://cdn.shopify.com/s/files/1/0622/6817/8611/files/swatch-metal-oil-rubbed-bronze.jpg?v=1663763047'
    },
    {
      name: 'Satin Nickel',
      src: 'https://cdn.shopify.com/s/files/1/0622/6817/8611/files/swatch-metal-satin-nickel.jpg?v=1663763119'
    },
    {
      name: 'Silver',
      src: 'https://cdn.shopify.com/s/files/1/0622/6817/8611/files/swatch-metal-silver.jpg?v=1663763157'
    },
    {
      name: 'Sun Gold',
      src: 'https://cdn.shopify.com/s/files/1/0622/6817/8611/files/swatch-metal-sungold.jpg?v=1663763179'
    },
    {
      name: 'White',
      src: 'https://cdn.shopify.com/s/files/1/0622/6817/8611/files/swatch-metal-white.jpg?v=1663763196'
    },
    {
      name: 'Cherry',
      src: 'https://cdn.shopify.com/s/files/1/0622/6817/8611/files/swatch-wood-cherry-finished.jpg?v=1663759567'
    },
    {
      name: 'Maple',
      src: 'https://cdn.shopify.com/s/files/1/0622/6817/8611/files/swatch-wood-maple-finished.jpg?v=1663759659'
    },
    {
      name: 'Red Oak',
      src: 'https://cdn.shopify.com/s/files/1/0622/6817/8611/files/swatch-wood-red-oak-finished.jpg?v=1663759711'
    },
    {
      name: 'White Oak',
      src: 'https://cdn.shopify.com/s/files/1/0622/6817/8611/files/swatch-wood-white-oak-finished.jpg?v=1663759761'
    },
    {
      name: 'Poplar',
      src: 'https://cdn.shopify.com/s/files/1/0622/6817/8611/files/swatch-wood-red-oak-finished.jpg?v=1663759711'
    }
  ]

  const url = `/collections/all/parent_id:${tag}?view=swatches-json`

  const response = await fetch(url, { method: 'GET' })

  if (!response.ok) {
  throw new Error(`HTTP error! status: ${response.status}`)
  }

  const rawData = await response.text()

  const data = Data.parse(JSON.parse(rawData))

  // console.log(`data ${tag}`,data)

  const replaceURLChars = (str: string) => {
    const hyphenIndex = str.indexOf('-')

    if (hyphenIndex !== -1) {
      const substringBeforeHyphen = str.substring(0, hyphenIndex)
      const replacedSubstring = substringBeforeHyphen.replace('16', '8')
      return replacedSubstring + str.substring(hyphenIndex)
    }
    return str
  }

  const getUniqueObjectsByFinish = (products) => {
    const uniqueFinishes = {}

    for (const product of products) {
      const finish = product.finish
        .replace(' Cast Aluminum', '')
        .replace(' Aluminum', '')
        .replace(' Cast Iron', '')
        .replace(' Steel', '')
        .replace(' Unfinished', '')
        .replace(' Finished', '')
        .replace('Flush UNF ', '')
        .replace(' Open', '')
        .replace(' Grille', '')
        .replace('Unfinished ', '')
        .replace('Finished ', '')

      const price = parseFloat(product['price'].replace(',', ''))

      if (isNaN(price)) {
        continue
      }

      // If current finish is not in uniqueFinishes object or if price of current object is lower then update object
      if (!uniqueFinishes[finish] || price < uniqueFinishes[finish].price) {
        uniqueFinishes[finish] = {
          title: product.title,
          handle: product.handle,
          finish: finish,
          url: product.url,
          price: price,
          type: product.type
        }
      }
    }

    const uniqueObjects = Object.values(uniqueFinishes)
    return uniqueObjects
  }

  const uniqData = getUniqueObjectsByFinish(data.swatches)


  uniqData.map((item) => {
    swatchesImages.map((swatch) => {
      if (item.finish === swatch.name) {
        item.src = swatch.src
      }
  
      if (item.type === 'Made to Order Grille Collection') {
        if (!item.title.includes('Clodagh')) {
          item.url = replaceURLChars(item.url)
        } else {
        }
      }
    })
  })

  // console.log("uniqData",uniqData)
  return uniqData
}

export const Product: FC<Props> = ({ hit }) => {
  const [swatches, setSwatches] = useState<Data['swatches']>([])
  const isParent: boolean = hit.named_tags.is_parent === 'yes'
  const parentId = hit.named_tags.parent_id
  const showQuickShipBadge =
    hit.named_tags['algolia-filter-quickship'] === 'yes'

    const clodaghValue = 
    (hit as any)['meta.custom.designer_collaboration'] || 
    hit.meta?.custom?.designer_collaboration
  const showClodaghBadge =
    !showQuickShipBadge && clodaghValue === 'Clodagh'

  //console.log(`Product >> hit: ${JSON.stringify(hit)}`)

  // swatches for parent products
  useEffect(() => {
    if (isParent === false) {
      return
    }

    fetchSwatches(parentId)
      .then((items) => {
        if (items.length > 0) {
          setSwatches(items)
        }
      })
      .catch((err) => {
        console.error("Swatches can't be fetched", err)
      })
  }, [isParent, parentId])

  return (
    <div className="AlgoliaProduct">
      <a href={'/products/' + hit?.handle} title={`${hit.handle} product link`}>
        <div className="AlgoliaProduct-imageContainer">
                  <img
            className="AlgoliaProduct-image additional-placeholder"
            src={getSizedImageUrl(hit?.product_image, '375x')}
            alt={hit.handle}
            loading="lazy"
          />
          {showQuickShipBadge && (
            <div className="AlgoliaProduct-quickShipBadge">
              <QuickShip stroke={'white'} /> Quick Ship
            </div>
          )}
          {showClodaghBadge && (
            <div className="AlgoliaProduct-ClodaghBadge">
              <Clodagh/>
            </div>
          )}
        </div>

        {hit.title && <div className="AlgoliaProduct-title">{hit.title}</div>}
      </a>

      {hit.named_tags.is_parent === 'yes' && swatches.length > 0 ? (
        <div className="AlgoliaProduct-swatchesContainer">
          <div className="AlgoliaProduct-swatchesLabel">
            {swatches.length} Color{swatches.length > 1 ? 's' : ''}
          </div>
          <div className="AlgoliaProduct-swatches">
            {swatches.map((item, i) => {
              return item.src ? (
                <a href={item.url} key={i}>
                  <img
                    className="AlgoliaProduct-swatch"
                    src={item?.src}
                    loading="lazy"
                  />
                </a>
              ) : null
            })}
          </div>
        </div>
      ) : (
        ''
      )}

      <YotpoReviews id={hit.id} handle={hit.handle} />
    </div>
  )
}
